import { Loader2, Mic, MicOff } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "./ui/Button.jsx";
import { Label } from "./ui/Label.jsx";
import { Textarea } from "./ui/TextArea.jsx";
import { useToast } from "../hooks/useToast.js";
import { parseVoiceUpdate, updateLead } from "../services/leadService.js";
import { fetchStages } from "../services/leadStageService.js";

export default function VoiceUpdateButton({ leadId, leadName, onUpdated }) {
  const { toast } = useToast();
  const [state, setState] = useState("idle"); // idle, recording, processing
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // Review popup state
  const [showReview, setShowReview] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewStage, setReviewStage] = useState("");
  const [reviewTranscript, setReviewTranscript] = useState("");
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [currentStageInfo, setCurrentStageInfo] = useState(null);

  // Load stages once
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchStages();
        setStages(res.data || []);
      } catch {}
    })();
  }, []);

  const startRecording = useCallback(async (e) => {
    e.stopPropagation();
    try {
      chunksRef.current = [];
      setState("recording");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/webm";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = () => processAudio();
      recorder.start(250);
    } catch {
      setState("idle");
      toast({ variant: "destructive", title: "Mic access denied" });
    }
  }, [toast]);

  const stopRecording = useCallback((e) => {
    e.stopPropagation();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const processAudio = useCallback(async () => {
    setState("processing");

    const chunks = chunksRef.current;
    if (!chunks.length || new Blob(chunks).size < 1000) {
      toast({ variant: "destructive", title: "Too short", description: "Speak for at least 2 seconds" });
      setState("idle");
      return;
    }

    const audioBlob = new Blob(chunks, { type: chunks[0].type || "audio/webm" });

    try {
      const parseRes = await parseVoiceUpdate(audioBlob, leadId);

      if (!parseRes.success || !parseRes.data) {
        toast({ variant: "destructive", title: "Could not understand", description: "Try again" });
        setState("idle");
        return;
      }

      const { intent, stage_key, note, current_stage, new_stage_label } = parseRes.data;

      // Build the note with transition info
      const noteParts = [];
      const isStageChange = (intent === "stage_change" || intent === "stage_and_note") && stage_key;

      if (isStageChange && current_stage && current_stage.key !== stage_key) {
        noteParts.push(`Stage: ${current_stage.label} → ${new_stage_label || stage_key.replace(/_/g, " ")}`);
      }
      if (note) noteParts.push(note);

      // Open review popup with pre-filled data
      setReviewNote(noteParts.join("\n"));
      setReviewStage(isStageChange && stage_key ? stage_key : (current_stage?.key || ""));
      setReviewTranscript(parseRes.transcript || "");
      setCurrentStageInfo(current_stage);
      setShowReview(true);
      setState("idle");
    } catch (err) {
      toast({ variant: "destructive", title: "Processing failed", description: err.message });
      setState("idle");
    }
  }, [leadId, toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePayload = {};

      // Stage change if different from current
      if (reviewStage && (!currentStageInfo || currentStageInfo.key !== reviewStage)) {
        updatePayload.deal_status = reviewStage;
      }

      // Add comment
      if (reviewNote.trim()) {
        updatePayload.comment = [{
          comment_text: reviewNote.trim(),
          contact_channel: reviewStage || null,
        }];
      }

      if (Object.keys(updatePayload).length === 0) {
        toast({ variant: "destructive", title: "Nothing to update" });
        setSaving(false);
        return;
      }

      await updateLead(leadId, updatePayload);

      toast({ title: `${leadName} updated` });
      setShowReview(false);
      onUpdated?.();
    } catch (err) {
      toast({ variant: "destructive", title: "Update failed", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <>
      {/* Mic Button */}
      {state === "processing" ? (
        <button
          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-purple-50 border border-purple-400"
          onClick={(e) => e.stopPropagation()}
        >
          <Loader2 size={14} className="text-purple-500 animate-spin" />
        </button>
      ) : state === "recording" ? (
        <button
          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-red-100 border border-red-500 animate-pulse"
          title="Tap to stop"
          onClick={stopRecording}
        >
          <MicOff size={14} className="text-red-600" />
        </button>
      ) : (
        <button
          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-purple-50 border border-purple-500 hover:bg-purple-100 transition active:scale-95"
          title="Voice update"
          onClick={startRecording}
        >
          <Mic size={14} className="text-purple-600" />
        </button>
      )}

      {/* Review Popup (Bottom Sheet) */}
      <AnimatePresence>
        {showReview && (
          <div
            className="fixed inset-0 z-[100]"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-xs"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => { e.stopPropagation(); setShowReview(false); }}
            />

            <motion.div
              key="voice-review"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 180, damping: 22, mass: 0.5 }}
              className="fixed bottom-0 left-0 right-0 w-full bg-white rounded-t-3xl shadow-lg p-5 z-[101]"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div
                className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1 w-16 rounded-full cursor-pointer"
                onClick={() => setShowReview(false)}
              />

              <h2 className="text-base font-semibold text-gray-800 mb-1 text-center">
                Review Update
              </h2>
              <p className="text-[10px] text-gray-400 text-center mb-4">{leadName}</p>

              {/* Transcript */}
              {reviewTranscript && (
                <div className="bg-gray-50 rounded-lg p-2 mb-3">
                  <p className="text-[10px] text-gray-500">
                    <span className="font-medium">Heard:</span> "{reviewTranscript}"
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {/* Lead Stage Dropdown — native select to avoid portal/z-index issues */}
                <div>
                  <Label className="text-xs font-medium">Lead Stage</Label>
                  <select
                    value={reviewStage}
                    onChange={(e) => setReviewStage(e.target.value)}
                    className="w-full h-8 text-xs mt-1 px-3 rounded-md border border-gray-200 bg-white text-gray-700 outline-none focus:ring-1 focus:ring-[#00a8b4]/30"
                  >
                    <option value="">No stage change</option>
                    {stages.map((s) => (
                      <option key={s.id} value={s.stage_key}>
                        {s.stage_label}
                      </option>
                    ))}
                  </select>
                  {currentStageInfo && currentStageInfo.key !== reviewStage && reviewStage && (
                    <p className="text-[10px] text-[#00a8b4] mt-1">
                      {currentStageInfo.label} → {stages.find((s) => s.stage_key === reviewStage)?.stage_label || reviewStage}
                    </p>
                  )}
                </div>

                {/* Comment / Note */}
                <div>
                  <Label className="text-xs font-medium">Comment</Label>
                  <Textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    rows={3}
                    className="text-xs mt-1"
                    placeholder="Add your comment..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowReview(false)}
                  className="h-8 px-3 text-xs rounded-full border border-gray-200 text-gray-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || (!reviewNote.trim() && (!reviewStage || reviewStage === currentStageInfo?.key))}
                  className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white"
                >
                  {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
